/**
 * One entry point for the Vue compatibility views.
 *
 * Composes the four verified services so a view never assembles the flow itself:
 * the server owns identity, inventory, price and payment state; this module only
 * sequences the calls and keeps the customer's local choices between them.
 */

// Explicit extensions: Node's ESM resolver needs them for the standalone service tests,
// and the bundler resolves the exact path just as well.
import { createCheckoutV2 } from './checkoutV2.js'
import { createSelectionFlow } from './checkoutV2Selection.js'
import { createCommitFlow } from './checkoutV2Commit.js'
import { createPaymentFlow, describeState, transferDetails } from './checkoutV2Payment.js'

export function createVueCheckout({ api = createCheckoutV2() } = {}) {
  const selection = createSelectionFlow(api)
  // The seats currently held by this flow, so the cart write reuses exactly them.
  let seats = []
  const commit = createCommitFlow(api, selection)
  const payment = createPaymentFlow(api)

  return {
    get checkoutId() { return selection.checkoutId },
    get identityEpoch() { return api.identityEpoch },

    /** Entry: the guest session exists before anything else is attempted. */
    async start(tripId, ticketTypeId) {
      const order = await selection.ensureDraft(tripId, ticketTypeId)
      const seats = await selection.loadAvailability(tripId)
      return { order, seats }
    },

    /** Steps 4-5: seats, then the services cart, each written with the current revision. */
    async chooseSeats(seatIds, { quantity } = {}) {
      const result = await selection.reserveSeats(seatIds, { quantity })
      seats = [...seatIds]
      return result
    },

    /** The cart is sent with the same seats and revision, so one write keeps them in step. */
    async chooseAddons(addons, { seatIds = seats, quantity } = {}) {
      return selection.reserveSeats(seatIds, { quantity, addons })
    },

    async applyDiscount(discountCode) {
      return commit.requestQuote(discountCode)
    },

    /** Step 7: create the order. The caller renders whatever the server answered. */
    async createOrder(passengers, { requiresPickup = true } = {}) {
      return commit.commit(passengers, { requiresPickup })
    },

    /** Step 8: only the server says what the payment state is. */
    async paymentState(checkoutId) {
      const view = await payment.load(checkoutId)
      return { view, details: transferDetails(view), state: describeState(view.state) }
    },

    async checkPayment(checkoutId, key) {
      return payment.check(checkoutId, key)
    },

    /** Leaving the flow, or switching trip, releases the held seats first. */
    async abandon() {
      return selection.release('changed')
    },
  }
}

/**
 * One instance per page load: SeatMap holds the seats, AddonServices writes the cart, and
 * both must address the same owned checkout.
 */
let shared = null

export function sharedVueCheckout() {
  if (shared === null) shared = createVueCheckout()
  return shared
}
